/**
 * Accessible Icon Button Component
 *
 * WCAG 2.2 Level AA compliant button for icon-only actions.
 * Ensures proper labeling for screen readers while maintaining visual design.
 *
 * Key Accessibility Features:
 * - aria-label for screen reader users
 * - sr-only text as backup
 * - Proper focus indicators (from global styles)
 * - aria-expanded for toggle states
 * - aria-haspopup for menu/dialog triggers
 *
 * Usage:
 * ```tsx
 * <AccessibleIconButton
 *   icon={<MenuIcon />}
 *   label="Toggle navigation menu"
 *   onClick={toggleMenu}
 *   aria-expanded={isMenuOpen}
 * />
 * ```
 */

import type React from 'react';

export interface AccessibleIconButtonProps {
  /** Icon element to display (e.g., Lucide icon) */
  icon: React.ReactNode;

  /** Accessible label for screen readers (required for accessibility) */
  label: string;

  /** Click handler */
  onClick?: () => void;

  /** Additional CSS classes */
  className?: string;

  /** Button type (default: "button") */
  type?: 'button' | 'submit' | 'reset';

  /** Disabled state */
  disabled?: boolean;

  /** Test ID for Playwright tests */
  'data-testid'?: string;

  /** ARIA: Indicates the expanded state of a collapsible element */
  'aria-expanded'?: boolean;

  /** ARIA: Indicates the element has a popup (menu, dialog, listbox, etc.) */
  'aria-haspopup'?: 'menu' | 'dialog' | 'listbox' | 'tree' | 'grid' | boolean;

  /** ARIA: Indicates the current state in a set of controls */
  'aria-pressed'?: boolean;

  /** Title tooltip (also used as fallback label) */
  title?: string;
}

/**
 * Accessible Icon Button
 *
 * Provides proper labeling for icon-only buttons to ensure screen reader
 * users can understand the button's purpose.
 */
export function AccessibleIconButton({
  icon,
  label,
  onClick,
  className = '',
  type = 'button',
  disabled = false,
  'data-testid': dataTestId,
  'aria-expanded': ariaExpanded,
  'aria-haspopup': ariaHaspopup,
  'aria-pressed': ariaPressed,
  title,
}: AccessibleIconButtonProps) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      aria-expanded={ariaExpanded}
      aria-haspopup={ariaHaspopup}
      aria-pressed={ariaPressed}
      title={title || label}
      data-testid={dataTestId}
      className={`inline-flex items-center justify-center p-2 rounded-lg
                  transition-colors duration-200
                  hover:bg-zinc-100 dark:hover:bg-zinc-800
                  focus:outline-hidden focus-visible:ring-2 focus-visible:ring-sky-500
                  disabled:opacity-50 disabled:cursor-not-allowed
                  ${className}`}
    >
      {/* Icon with aria-hidden to prevent duplicate announcements */}
      <span aria-hidden="true">{icon}</span>

      {/* Screen reader only text as backup */}
      <span className="sr-only">{label}</span>
    </button>
  );
}

/**
 * Default export for convenience
 */
export default AccessibleIconButton;
